import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { WorkflowPrecheckResponse } from '../../services/legacyApi';
import { FlowActionImpactList } from './FlowActionImpactList';

type FlowPrecheckDialogProps = {
    open: boolean;
    result: WorkflowPrecheckResponse | null;
    onClose: () => void;
    onContinue: () => void;
    continueDisabled?: boolean;
};

const badgeClass = (status: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'fail') return 'bg-red-100 text-red-700 border border-red-200';
    if (normalized === 'warn') return 'bg-amber-100 text-amber-800 border border-amber-200';
    return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
};

export const FlowPrecheckDialog: React.FC<FlowPrecheckDialogProps> = ({
    open,
    result,
    onClose,
    onContinue,
    continueDisabled = false,
}) => {
    const status = (result?.overall_status || 'pass').toLowerCase();
    const canContinue = status !== 'fail' && !continueDisabled;

    return (
        <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Workflow Precheck</DialogTitle>
                    <DialogDescription>
                        Review the checks before running this transition.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badgeClass(status)}`}>
                        {status.toUpperCase()}
                    </div>
                    <div className="space-y-2">
                        {(result?.checks || []).map((check) => (
                            <div key={check.key} className="rounded border p-2">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium text-slate-900">{check.key.replace(/_/g, ' ')}</p>
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass(check.status)}`}>
                                        {(check.status || 'pass').toUpperCase()}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-600">{check.message}</p>
                            </div>
                        ))}
                    </div>
                    <FlowActionImpactList effects={result?.branch_effect_details || []} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={onContinue} disabled={!canContinue}>
                        Continue
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

